import { Injectable, inject } from '@angular/core';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

export interface DraftAttachment {
  fileUrl: string;
  attachedFilename: string;
  size: number;
}

export interface EmailDraft {
  to: string;
  subject: string;
  content: string;
  cc: string[];
  bcc: string[];
  attachments: DraftAttachment[];
  timestamp: number;
}

const DRAFT_STORAGE_KEY_PREFIX = 'crm_email_draft_';
const DRAFT_EXPIRY_DAYS = 7; // Drafts expire after 7 days

@Injectable({
  providedIn: 'root',
})
export class SendEmailService {
  private readonly confirmationModal = inject(OpenConfirmationModalService);

  /**
   * Get a normalized key for the "to" address(es)
   * Normalizes by sorting multiple emails and converting to lowercase
   */
  private getDraftKey(to: string): string {
    if (!to) return '';
    const emails = to
      .split(',')
      .map((email) => email.trim().toLowerCase())
      .filter((email) => email.length > 0)
      .sort();
    return emails.join(',');
  }

  /**
   * Get the localStorage key for a draft
   */
  private getStorageKey(draftKey: string): string {
    return `${DRAFT_STORAGE_KEY_PREFIX}${draftKey}`;
  }

  /**
   * Save draft email to local storage as a separate entry
   */
  saveDraft(
    to: string,
    subject: string,
    content: string,
    cc: string[] = [],
    bcc: string[] = [],
    attachments: DraftAttachment[] = [],
  ): void {
    if (!to || (!subject && !content)) {
      return;
    }

    const draftKey = this.getDraftKey(to);
    if (!draftKey) {
      return;
    }

    const draft: EmailDraft = {
      to,
      subject: subject || '',
      content: content || '',
      cc,
      bcc,
      attachments,
      timestamp: Date.now(),
    };

    const storageKey = this.getStorageKey(draftKey);
    try {
      localStorage.setItem(storageKey, JSON.stringify(draft));
    } catch (error) {
      console.error('Error saving draft to localStorage:', error);
    }
  }

  /**
   * Get draft email for a specific "to" address
   */
  getDraft(to: string): EmailDraft | null {
    const draftKey = this.getDraftKey(to);
    if (!draftKey) {
      return null;
    }

    const storageKey = this.getStorageKey(draftKey);
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) {
        return null;
      }

      const draft: EmailDraft = JSON.parse(stored);
      draft.cc = draft.cc || [];
      draft.bcc = draft.bcc || [];
      draft.attachments = draft.attachments || [];

      const daysSinceCreated = (Date.now() - draft.timestamp) / (1000 * 60 * 60 * 24);
      if (daysSinceCreated > DRAFT_EXPIRY_DAYS) {
        this.deleteDraft(to);
        return null;
      }

      return draft;
    } catch (error) {
      console.error('Error reading draft from localStorage:', error);
      return null;
    }
  }

  /**
   * Delete draft for a specific "to" address
   */
  deleteDraft(to: string): void {
    const draftKey = this.getDraftKey(to);
    if (!draftKey) {
      return;
    }

    const storageKey = this.getStorageKey(draftKey);
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Error deleting draft from localStorage:', error);
    }
  }

  /**
   * Ask user if they want to restore a draft
   */
  askToRestoreDraft(to: string): Observable<boolean> {
    const draft = this.getDraft(to);
    if (!draft) {
      return of(false);
    }

    return this.confirmationModal
      .openModal({
        type: 'confirm',
        title: 'CRM.RESTORE_DRAFT_TITLE',
        message: 'CRM.RESTORE_DRAFT_MESSAGE',
        confirmButtonText: 'CRM.RESTORE',
        cancelButtonText: 'CRM.DISCARD',
      })
      .pipe(map((confirmed) => !!confirmed));
  }

  /**
   * Clean up expired drafts from localStorage
   * This can be called periodically or on initialization
   */
  cleanExpiredDrafts(): void {
    const now = Date.now();
    const expiryMs = DRAFT_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    try {
      // Iterate through all localStorage keys
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (key && key.startsWith(DRAFT_STORAGE_KEY_PREFIX)) {
          try {
            const stored = localStorage.getItem(key);
            if (stored) {
              const draft: EmailDraft = JSON.parse(stored);
              if (now - draft.timestamp > expiryMs) {
                localStorage.removeItem(key);
              }
            }
          } catch (error) {
            // If we can't parse a draft, remove it
            console.warn(`Removing invalid draft entry: ${key}`, error);
            localStorage.removeItem(key);
          }
        }
      }
    } catch (error) {
      console.error('Error cleaning expired drafts:', error);
    }
  }
}
